import { gql } from "@apollo/client";
import { apolloCore } from "src/utils/apollo/apollo";
import {
  ExecuteQueryV3Mutation,
  ExecuteQueryV3MutationVariables,
  ExecuteQueryV3Document,
} from "src/gql/codegen/graphql";

import { pick } from "lodash";

import type { Session } from "src/utils/users/types";
import type { Parameter } from "src/utils/parameters/types";
import type { ContextOwner } from "src/gql/codegen/graphql";
import { SupportedPublicPerformanceTierKey } from "shared/SelectPerformance/useGetPerformanceTiers";

export type SupportedExecutionTypes =
  | "interactive"
  | "viz_custom_params_refresh";

interface Args {
  queryId: number;
  parameters: Parameter[];
  requestContext: { session?: Session; apiKey?: string };
  executionContext: ContextOwner;
  executionType: SupportedExecutionTypes;
  performance: SupportedPublicPerformanceTierKey | undefined;
  metadata?: Record<string, any>;
}

export async function callExecuteQueryV3(args: Args) {
  const {
    queryId,
    parameters,
    requestContext,
    executionContext,
    executionType,
    performance,
    metadata,
  } = args;

  return await apolloCore.mutate<
    ExecuteQueryV3Mutation,
    ExecuteQueryV3MutationVariables
  >({
    mutation: ExecuteQueryV3Document,
    context: requestContext,
    variables: {
      query_id: queryId,
      parameters,
      /*
       * `pick` to ensure we don't have unnecessary properties on
       * `ContextOwner`. Extra props passes TS checks, but it makes the
       * GraphQL mutation fail.
       */
      executor: pick(executionContext, ["id", "type"]),
      performance,
      executionType,
      metadata,
    },
    fetchPolicy: "no-cache",
  });
}

gql`
  mutation ExecuteQueryV3(
    $query_id: Int!
    $executor: ContextOwner!
    $performance: String
    $parameters: [Parameter!]!
    $executionType: String!
    $metadata: json
  ) {
    execute_query_v3(
      query_id: $query_id
      executor: $executor
      performance: $performance
      parameters: $parameters
      execution_type: $executionType
      metadata: $metadata
    ) {
      job_id
    }
  }
`;
